/* eslint-disable react/prop-types */
import type {
    ChangeEvent,
    ReactElement,
    ReactNode,
    SyntheticEvent,
} from 'react';
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import type { CompositionFeType } from './utils';
import type { RawMaterial } from '@buying/library';
import type { AutocompleteChangeReason } from '@mui/material/Autocomplete';
import type { StackProps } from '@mui/material/Stack';

import { HighlightText } from '@bestseller-bit/frontend-community.components.highlight-text';
import { TrashIcon } from '@bestseller-bit/frontend-community.theme.buying-theme';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import debounce from 'lodash/debounce';

import { useRawMaterials } from '@buying/library';

import { MaterialOnTheFlyValueContext } from './reducer';

interface RawMaterialRowProps {
    composition: CompositionFeType;
    disableDelete?: boolean;
    sx?: StackProps['sx'];
    onCompositionChange: (newComposition: CompositionFeType) => void;
    onDelete: (feId: string) => void;
    allSelectedCompositions: Array<CompositionFeType>;
}

const RawMaterialRow = ({
    composition,
    disableDelete = false,
    sx,
    onCompositionChange: handleCompositionChange,
    onDelete: handleDelete,
    allSelectedCompositions,
}: RawMaterialRowProps): ReactElement => {
    const { data, isLoading, fetchMore, generalQuery, setGeneralQuery } =
        useRawMaterials();
    const { materialOnTheFlyValue } = useContext(MaterialOnTheFlyValueContext);

    //as percentage is a number in the backend, we need to convert it to a string
    const [percentage, setPercentage] = useState<string>(
        composition.percentage?.toString() ?? ''
    );

    const disableFields = !materialOnTheFlyValue.materialSubcategory;
    // If a previously selected raw material is deleted/unavailable in current options, mark it as invalid
    const isRawMaterialAvailable = !composition.rawMaterial?.isDeleted;

    const handlePercentageBlur = (): void => {
        const newComposition = {
            ...composition,
            percentage: percentage ? Number(percentage) : undefined,
        };
        handleCompositionChange(newComposition);
    };

    const handlePercentageChange = (
        event: ChangeEvent<HTMLInputElement>
    ): void => {
        let newValue = event.target.value;
        // Remove all non-numeric characters
        newValue = newValue.replace(/\D/g, '');
        // Ensure the value is between 1 and 100
        if (Number(event.target.value) < 1) {
            newValue = '';
        }
        if (Number(event.target.value) > 100) {
            newValue = '100';
        }
        setPercentage(newValue);
    };

    const handleSelect = (
        _event: SyntheticEvent | null,
        value: {
            ['__typename']: 'RawMaterialEdge' | undefined;
            cursor: string;
            node: RawMaterial;
        } | null,
        reason: AutocompleteChangeReason
    ): void => {
        const newComposition = {
            ...composition,
            rawMaterial: reason === 'clear' || !value ? undefined : value.node,
        };
        handleCompositionChange(newComposition);
    };

    //This fn is responsable for delete the raw and deselect the selection in the dropdown menu
    const handleClickTrashIconInRawMaterial = (): void => {
        handleDelete(composition.feId);

        const event = {
            target: { value: composition.rawMaterial?.rawMaterialName || '' }, //rawMaterialPublicId
        } as ChangeEvent<HTMLSelectElement>;

        handleSelect(event, null, 'clear');
    };

    // Debounce is used to avoid making a request for every key stroke (throttling)
    const debouncedSetGeneralQuery = debounce(setGeneralQuery, 300);
    const handleRawMaterialInputChange = (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        debouncedSetGeneralQuery(e.target.value);
    };

    // This observer is used to fetch more rawMaterials when the last rawMaterial comes into view
    const observer = useRef<IntersectionObserver | null>(null);
    const lastRawMaterialRef = useCallback(
        (node: HTMLLIElement | null) => {
            // If the data is still loading, return early
            if (isLoading) return;

            // Disconnect the previous IntersectionObserver instance if it exists
            if (observer.current) observer.current.disconnect();

            // Create a new IntersectionObserver instance
            observer.current = new IntersectionObserver((entries) => {
                // When the last raw material element is intersecting the viewport, fetch more data
                if (entries[0].isIntersecting) {
                    fetchMore();
                }
            });

            // Observe the last raw material element
            if (node) observer.current.observe(node);
        },
        [isLoading, fetchMore]
    );

    // This fn is in charge to filter the value in the dropdown menu
    const filteredOptions = useMemo(() => {
        // Store unique values of rawMaterialName from all selected compositions
        const selectedRawMaterialNames = new Set(
            allSelectedCompositions.map(
                (selectedComposition) =>
                    selectedComposition.rawMaterial?.rawMaterialName
            )
        );
        return (data?.rawMaterials?.edges ?? []).filter(
            (option) =>
                option &&
                !selectedRawMaterialNames.has(option.node.rawMaterialName)
        );
    }, [data, allSelectedCompositions]);

    // Sync local state when composition.percentage changes from outside
    // (e.g. optional -> required should update from '' to '100').
    useEffect(() => {
        setPercentage(composition.percentage?.toString() ?? '');
    }, [composition.percentage]);

    return (
        <Stack sx={sx}>
            <Stack
                direction="row"
                spacing={10}
                sx={{
                    alignItems: 'center',
                }}
            >
                <Autocomplete
                    id={`bom-card-mof-composition-select-${composition.feId}`}
                    options={filteredOptions}
                    loading={isLoading}
                    getOptionLabel={(option): string =>
                        option?.node.rawMaterialName ?? ''
                    }
                    sx={{
                        width: 1,
                        [`& .${autocompleteClasses.popupIndicator}`]: {
                            ['&:hover']: {
                                backgroundColor: 'transparent', // Remove hover background
                            },
                        },
                    }}
                    //MUI way to pass props to internal components
                    slotProps={{
                        clearIndicator: { title: '' }, // Remove clearIndicator's tooltip
                        popupIndicator: { title: '' }, // Remove popupIndicator's tooltip
                    }}
                    value={
                        composition.rawMaterial
                            ? {
                                  ['__typename']: undefined,
                                  cursor: '',
                                  node: composition.rawMaterial,
                              }
                            : null
                    }
                    disabled={disableFields}
                    renderInput={(params): ReactNode => (
                        <TextField
                            sx={{ width: '448px' }}
                            autoFocus
                            {...params}
                            error={!isRawMaterialAvailable}
                            placeholder="Select"
                            value={generalQuery}
                            onChange={handleRawMaterialInputChange}
                        />
                    )}
                    filterOptions={(x) => x} // Needed when doing async filtering. MUI docs: https://mui.com/material-ui/react-autocomplete/#search-as-you-type
                    renderOption={(props, option, state) => (
                        <li
                            {...props}
                            ref={
                                // @ts-expect-error - Element implicitly has an 'any' type because expression of type '"data-option-index"' can't be used to index type 'HTMLAttributes<HTMLLIElement> & { key: any; }'. Property 'data-option-index' does not exist on type 'HTMLAttributes<HTMLLIElement> & { key: any; }'.
                                props['data-option-index'] ===
                                (data?.rawMaterials?.edges?.length ?? 0) - 1
                                    ? lastRawMaterialRef
                                    : null
                            }
                        >
                            <HighlightText
                                text={option?.node.rawMaterialName ?? ''}
                                highlight={state.inputValue}
                            />
                        </li>
                    )}
                    disablePortal
                    onChange={handleSelect}
                    onBlur={(): void => {
                        setGeneralQuery('');
                    }}
                />
                <TextField
                    placeholder=""
                    // We remove the type="number"
                    // Seems as if input fields dont recoqnize commas, dots and "e" as a change,
                    // so the onChange is never fired when pressing them.
                    value={percentage}
                    disabled={disableFields}
                    type="text"
                    sx={{
                        ['input']: {
                            textAlign: 'end',
                        },
                        /* Chrome, Safari, Edge, Opera */
                        [`
            input::-webkit-outer-spin-button,
            input::-webkit-inner-spin-button
          `]: {
                            ['-webkit-appearance']: 'none',
                            margin: 0,
                        },

                        /* Firefox */
                        ['input[type=number]']: {
                            ['-moz-appearance']: 'textfield',
                        },
                    }}
                    onBlur={handlePercentageBlur}
                    // onKeyDown={handleKeyDown}
                    onChange={handlePercentageChange}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    %
                                </InputAdornment>
                            ),
                        },
                    }}
                />
                <IconButton
                    variant="text"
                    sx={{
                        minWidth: '0 !important',
                        padding: '0px !important',
                        ['&:hover']: {
                            backgroundColor: 'transparent',
                        },
                    }}
                    disabled={disableDelete}
                    onClick={handleClickTrashIconInRawMaterial}
                >
                    <TrashIcon />
                </IconButton>
            </Stack>
            {!isRawMaterialAvailable && (
                <FormHelperText error>
                    Raw material is not available
                </FormHelperText>
            )}
        </Stack>
    );
};

export { RawMaterialRow };
export type { RawMaterialRowProps };
